import { ActionContext, ActionTree } from 'vuex';
import eventBus from '@/services/globals/events-bus.service';

import { RootState } from '@/store';
import situacaoFinalService, {
  ETAPA_RESULTADO_FINAL,
  TrabalhoSituacaoFinalModel,
} from '@/services/academico/situacaofinal/situacao-final.service';
import { AcompanhamentoAtaActionTypes } from './action-types';
import { AcompanhamentoAtaMutationTypes } from './mutation-types';
import { Mutations } from './mutations';
import { lerPersistido, State } from './state';

/** De quanto em quanto tempo o andamento é relido do servidor. */
const INTERVALO_DE_CONSULTA_MS = 2000;

/**
 * O relógio do acompanhamento.
 *
 * Fora do state de propósito: é um detalhe de execução, não informação de tela — pôr um handle de
 * `setTimeout` no state reativo só faria o Vuex disparar renderização a cada ciclo.
 *
 * É um só, e isso é intencional: existe UM acompanhamento por vez, o do trabalho que a caixa está
 * mostrando. Iniciar outro cancela o anterior.
 */
let proximaConsulta = 0;

function pararConsulta(): void {
  if (proximaConsulta) {
    window.clearTimeout(proximaConsulta);
    proximaConsulta = 0;
  }
}

/** O que a tela da ata entrega para iniciar um cálculo. */
export type PedidoDeAcompanhamentoAta = {
  turmas: number[];
  codigoEscola: number;
  ano: number;
  semestre: number;
  filtrosRelatorio: string | null;
  codigoRelatorio: string;
  titulo: string;
};

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1],
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [AcompanhamentoAtaActionTypes.ACTION_INICIAR](
    contexto: AugmentedActionContext,
    pedido: PedidoDeAcompanhamentoAta,
  ): Promise<void>;
  [AcompanhamentoAtaActionTypes.ACTION_ACOMPANHAR](
    contexto: AugmentedActionContext,
    idTrabalho: string,
  ): Promise<void>;
  [AcompanhamentoAtaActionTypes.ACTION_RESTAURAR](
    contexto: AugmentedActionContext,
  ): Promise<void>;
  [AcompanhamentoAtaActionTypes.ACTION_ADOTAR](
    contexto: AugmentedActionContext,
    trabalho: TrabalhoSituacaoFinalModel,
  ): Promise<void>;
  [AcompanhamentoAtaActionTypes.ACTION_MOSTRAR](contexto: AugmentedActionContext): void;
  [AcompanhamentoAtaActionTypes.ACTION_ALTERNAR_RECOLHIDA](
    contexto: AugmentedActionContext,
  ): void;
  [AcompanhamentoAtaActionTypes.ACTION_ENCERRAR](contexto: AugmentedActionContext): void;
}

export const actions: ActionTree<State, RootState> & Actions = {
  /**
   * Enfileira o cálculo do resultado final e passa a acompanhá-lo.
   *
   * Quando TODAS as turmas pedidas já estão em cálculo — porque outra pessoa da secretaria disparou
   * antes —, o servidor não cria trabalho novo e devolve o que já existe. Acompanhamos ESSE: a
   * conta é a mesma, e recalcular por cima seria duas gravações disputando a mesma matrícula.
   */
  async [AcompanhamentoAtaActionTypes.ACTION_INICIAR](
    { commit, dispatch },
    pedido: PedidoDeAcompanhamentoAta,
  ) {
    pararConsulta();

    try {
      const enfileiramento = await situacaoFinalService.calcular({
        codigoEscola: pedido.codigoEscola,
        ano: pedido.ano,
        semestre: pedido.semestre,
        etapa: ETAPA_RESULTADO_FINAL,
        turmas: pedido.turmas,
        // A ata é a única consumidora da TEMPATAFECHAMENTO, e alguns contratos a leem para
        // integrar com outros sistemas. Ver RepovoarAtaFechamentoService, no backend.
        repovoarAtaFechamento: true,
        /*
          Os dados do relatório vão TAMBÉM para a tarefa, além do store.

          A caixa flutuante continua funcionando como sempre (store + localStorage) — o que muda é
          que agora existe um segundo lugar, persistido no servidor, de onde a ata pode ser
          reemitida: a janela "Minhas Tarefas". É o que faz a reemissão sobreviver ao F5, à troca
          de máquina e ao fim do dia, sem recalcular.
        */
        codigoRelatorio: pedido.codigoRelatorio,
        filtrosRelatorio: pedido.filtrosRelatorio,
        tituloRelatorio: pedido.titulo,
      });

      const id = enfileiramento.idTrabalho ?? enfileiramento.trabalhosEmAndamento?.[0] ?? null;

      if (!id) {
        commit(AcompanhamentoAtaMutationTypes.SET_ERRO, 'Não foi possível iniciar o cálculo.');

        return;
      }

      commit(AcompanhamentoAtaMutationTypes.SET_TRABALHO, {
        idTrabalho: id,
        filtrosRelatorio: pedido.filtrosRelatorio,
        codigoRelatorio: pedido.codigoRelatorio,
        codigoEscola: pedido.codigoEscola,
        ano: pedido.ano,
        semestre: pedido.semestre,
        titulo: pedido.titulo,
      });

      // Faz o item "Minhas Tarefas" aparecer no menu do usuário na hora.
      eventBus.emit('tarefaRegistrada');

      await dispatch(AcompanhamentoAtaActionTypes.ACTION_ACOMPANHAR, id);
    } catch {
      commit(
        AcompanhamentoAtaMutationTypes.SET_ERRO,
        'Não foi possível iniciar o cálculo do resultado final. A ata pode ser emitida com o último resultado gravado.',
      );
    }
  },

  /**
   * Lê o andamento e reagenda a próxima leitura enquanto o trabalho não termina.
   *
   * O relógio vive no módulo, não no componente: é o que permite ao usuário fechar a caixa, navegar
   * para outra tela e continuar sabendo quando o cálculo terminou.
   */
  async [AcompanhamentoAtaActionTypes.ACTION_ACOMPANHAR]({ commit, dispatch }, idTrabalho: string) {
    pararConsulta();

    try {
      const trabalho = await situacaoFinalService.acompanhar(idTrabalho);

      commit(AcompanhamentoAtaMutationTypes.SET_ANDAMENTO, trabalho);

      if (trabalho.terminou) return;
    } catch {
      // O trabalho expirou no servidor (a retenção é de meia hora depois do fim) ou ficou
      // inacessível. Paramos de consultar em vez de deixar a caixa girando para sempre — o cálculo
      // em si já aconteceu, e a ata sai com o que estiver gravado.
      commit(
        AcompanhamentoAtaMutationTypes.SET_ERRO,
        'O acompanhamento foi perdido. A ata pode ser emitida com o último resultado gravado.',
      );

      return;
    }

    proximaConsulta = window.setTimeout(
      () => dispatch(AcompanhamentoAtaActionTypes.ACTION_ACOMPANHAR, idTrabalho),
      INTERVALO_DE_CONSULTA_MS,
    );
  },

  /**
   * Retoma o acompanhamento guardado no navegador.
   *
   * É o que faz o F5 não perder nada: o cálculo nunca dependeu da aba, e o que o recarregamento
   * apagava era só o número do trabalho.
   */
  async [AcompanhamentoAtaActionTypes.ACTION_RESTAURAR]({ dispatch, state }) {
    const persistido = lerPersistido();

    if (!persistido || !state.idTrabalho) return;

    await dispatch(AcompanhamentoAtaActionTypes.ACTION_ACOMPANHAR, state.idTrabalho);
  },

  /**
   * Adota um trabalho que já estava rodando — o que a tela da ata encontra ao abrir.
   *
   * Não traz filtros de relatório: a emissão pertence a quem montou os filtros. Quem adota está
   * apenas acompanhando, e é justamente quem NÃO deve disparar um segundo cálculo por cima.
   */
  async [AcompanhamentoAtaActionTypes.ACTION_ADOTAR](
    { commit, dispatch },
    trabalho: TrabalhoSituacaoFinalModel,
  ) {
    commit(AcompanhamentoAtaMutationTypes.SET_TRABALHO, {
      idTrabalho: trabalho.id,
      filtrosRelatorio: null,
      codigoRelatorio: '',
      codigoEscola: trabalho.codigoEscola,
      ano: trabalho.ano,
      semestre: trabalho.semestre,
      titulo: 'Ata de Resultados Finais',
    });

    await dispatch(AcompanhamentoAtaActionTypes.ACTION_ACOMPANHAR, trabalho.id);
  },

  [AcompanhamentoAtaActionTypes.ACTION_MOSTRAR]({ commit }) {
    commit(AcompanhamentoAtaMutationTypes.MOSTRAR, undefined);
  },

  [AcompanhamentoAtaActionTypes.ACTION_ALTERNAR_RECOLHIDA]({ commit }) {
    commit(AcompanhamentoAtaMutationTypes.ALTERNAR_RECOLHIDA, undefined);
  },

  /**
   * Fecha e esquece. **Só o usuário faz isso** — nem o fim do cálculo, nem a troca de tela, nem o
   * F5: a caixa fica disponível com a emissão liberada até ele decidir que terminou.
   */
  [AcompanhamentoAtaActionTypes.ACTION_ENCERRAR]({ commit }) {
    pararConsulta();
    commit(AcompanhamentoAtaMutationTypes.LIMPAR, undefined);
  },
};
